import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';

import BoardContext from 'contexts/BoardContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import programHasSubPeriods from 'contextProviders/BonusAppProvider/programHasSubPeriods';
import isPerformanceBoard from 'isPerformanceBoard';

import sectionTypeCheckers from '../../../PerformanceBoardSlideOut/sectionTypeCheckers';
import PerformanceBoardSettingsContext from '../../contexts/PerformanceBoardSettingsContext';
import useUpdateGeneral from '../hooks/useUpdateGeneral';
import BoardOverridesSettings from './BoardOverridesSettings';

const BoardOverridesSettingsContainer = ({
  board,
}: {
  board: PerformanceBoardTypes.Board;
}) => {
  const { setHasUnsavedChanges, hasUnsavedChanges } = useContext(
    PerformanceBoardSettingsContext,
  );
  const updateGeneral = useUpdateGeneral();

  // Defaults on: only an explicit false hides the slideout Override button.
  const [isSlideoutOverrideButtonEnabled, setIsSlideoutOverrideButtonEnabled] =
    useState<boolean>(() => board.isSlideoutOverrideButtonEnabled !== false);
  const [isSlideoutOverrideEnabled, setIsSlideoutOverrideEnabled] =
    useState<boolean>(() => !!board.isSlideoutOverrideEnabled);
  const [
    isWeeklyFactTableOverrideEnabled,
    setIsWeeklyFactTableOverrideEnabled,
  ] = useState<boolean>(() => !!board.isWeeklyFactTableOverrideEnabled);
  const [
    isDisqualificationReasonsEnabled,
    setIsDisqualificationReasonsEnabled,
  ] = useState<boolean>(() => !!board.isDisqualificationReasonsEnabled);

  // The weekly fact-table override flow only applies when sub-periods are
  // actually involved: the board's bonus program uses the sub-period pattern AND
  // the board's slideout renders a bonus fact table (a reward / total reward /
  // eligibility section — the only sections that show per-week sub-period rows).
  // A board merely sitting on a sub-period dataset without those sections isn't
  // offered the toggle.
  const { getBonusProgram } = useContext(DatasetDefinitionsContext);
  const isSubPeriodBonusBoard = useMemo(() => {
    if (!programHasSubPeriods(getBonusProgram(board.dataType))) {
      return false;
    }
    return board.slideout.some(
      (section) =>
        sectionTypeCheckers.isBonusRewardSection(section) ||
        sectionTypeCheckers.isBonusTotalRewardSection(section) ||
        sectionTypeCheckers.isBonusEligibilitySection(section),
    );
  }, [getBonusProgram, board.dataType, board.slideout]);
  const bonusProgram = getBonusProgram(board.dataType);

  const draftBoard = useMemo(
    () => ({
      ...board,
      isSlideoutOverrideButtonEnabled,
      isSlideoutOverrideEnabled,
      isWeeklyFactTableOverrideEnabled,
      isDisqualificationReasonsEnabled,
    }),
    [
      board,
      isSlideoutOverrideButtonEnabled,
      isSlideoutOverrideEnabled,
      isWeeklyFactTableOverrideEnabled,
      isDisqualificationReasonsEnabled,
    ],
  );

  useEffect(() => {
    setHasUnsavedChanges(!_.isEqual(draftBoard, board));
  }, [draftBoard, board, setHasUnsavedChanges]);

  const handleSave = useCallback(() => {
    updateGeneral({ draftBoard });
  }, [updateGeneral, draftBoard]);

  return (
    <BoardOverridesSettings
      isSlideoutOverrideButtonEnabled={isSlideoutOverrideButtonEnabled}
      setIsSlideoutOverrideButtonEnabled={setIsSlideoutOverrideButtonEnabled}
      isSlideoutOverrideEnabled={isSlideoutOverrideEnabled}
      setIsSlideoutOverrideEnabled={setIsSlideoutOverrideEnabled}
      isSubPeriodBonusBoard={isSubPeriodBonusBoard}
      isWeeklyFactTableOverrideEnabled={isWeeklyFactTableOverrideEnabled}
      setIsWeeklyFactTableOverrideEnabled={setIsWeeklyFactTableOverrideEnabled}
      isDisqualificationReasonsEnabled={isDisqualificationReasonsEnabled}
      setIsDisqualificationReasonsEnabled={setIsDisqualificationReasonsEnabled}
      bonusProgram={bonusProgram}
      hasUnsavedChanges={hasUnsavedChanges}
      handleSave={handleSave}
    />
  );
};

const Gate = () => {
  const { board } = useContext(BoardContext);
  if (isPerformanceBoard(board)) {
    return <BoardOverridesSettingsContainer board={board} />;
  }
  return null;
};

export default Gate;
